import * as THREE from 'three';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js';
function mesh(name, geometry, material) {
    const result = new THREE.Mesh(geometry, material);
    result.name = name;
    result.castShadow = true;
    result.receiveShadow = true;
    return result;
}
// Seeded, subtle clay microstructure. No external texture dependency.
function clayTexture() {
    const size = 256;
    const heights = new Uint8ClampedArray(size * size);
    let seed = 9026;
    for (let y = 0; y < size; y++)
        for (let x = 0; x < size; x++) {
            seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
            heights[y * size + x] = 126 + (seed / 4294967296 - 0.5) * 42 + 5 * Math.sin(y * 1.3);
        }
    const normals = new Uint8ClampedArray(size * size * 4);
    const heightAt = (x, y) => heights[((y + size) % size) * size + ((x + size) % size)] / 255;
    for (let y = 0; y < size; y++)
        for (let x = 0; x < size; x++) {
            const normal = new THREE.Vector3((heightAt(x - 1, y) - heightAt(x + 1, y)) * 1.5, (heightAt(x, y - 1) - heightAt(x, y + 1)) * 1.5, 1).normalize();
            normals.set([(normal.x * .5 + .5) * 255, (normal.y * .5 + .5) * 255, (normal.z * .5 + .5) * 255, 255], (y * size + x) * 4);
        }
    const texture = new THREE.DataTexture(new Uint8Array(normals.buffer), size, size, THREE.RGBAFormat);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(4, 2);
    texture.flipY = true;
    texture.magFilter = THREE.LinearFilter;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.generateMipmaps = true;
    texture.needsUpdate = true;
    return texture;
}
function lathe(profile, material, name) {
    const points = profile.map(([r, y]) => new THREE.Vector2(r, y));
    return mesh(name, new THREE.LatheGeometry(points, 112), material);
}
function tube(points, radius, material, name) {
    return mesh(name, new THREE.TubeGeometry(new THREE.CatmullRomCurve3(points), 36, radius, 10, false), material);
}
// Two curved surfaces close at the edge: an actual tapered leaf volume.
function leafGeometry(length, width) {
    const vertices = [], uvs = [], indices = [];
    const rows = 34, columns = 16;
    const stride = columns + 1, layer = (rows + 1) * stride;
    for (let side = 0; side < 2; side++) {
        for (let i = 0; i <= rows; i++) {
            const t = i / rows;
            const halfWidth = width * Math.pow(Math.sin(Math.PI * t), 0.86);
            for (let j = 0; j <= columns; j++) {
                const across = 2 * j / columns - 1;
                const x = across * halfWidth;
                const y = t * length;
                const bend = 0.16 * Math.sin(t * Math.PI * 0.9);
                const ridge = 0.095 * (1 - Math.abs(across)) * Math.sin(Math.PI * t);
                const thickness = 0.024 * Math.sin(Math.PI * t) * (1 - across * across);
                vertices.push(x, y, bend + ridge + (side === 0 ? thickness : -thickness));
                uvs.push(j / columns, t);
            }
        }
    }
    for (let side = 0; side < 2; side++)
        for (let i = 0; i < rows; i++)
            for (let j = 0; j < columns; j++) {
                const a = side * layer + i * stride + j, b = a + stride;
                if (side === 0)
                    indices.push(a, a + 1, b, a + 1, b + 1, b);
                else
                    indices.push(a, b, a + 1, a + 1, b, b + 1);
            }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
}
function makeLeaf(name, length, width, rotation, y, materials) {
    const group = new THREE.Group();
    group.name = name;
    group.position.set(0, y, 0.025);
    group.rotation.z = rotation;
    group.add(mesh(`${name}Blade`, leafGeometry(length, width), materials.leaf));
    const veinPoints = [];
    for (let i = 0; i <= 16; i++) {
        const t = i / 16;
        veinPoints.push(new THREE.Vector3(0, t * length * 0.92, 0.16 * Math.sin(t * 0.92 * Math.PI * 0.9) + 0.119 * Math.sin(Math.PI * t * 0.92) + 0.002));
    }
    group.add(tube(veinPoints, 0.006, materials.vein, `${name}Midrib`));
    for (const t of [0.29, 0.43, 0.57, 0.69])
        for (const sign of [-1, 1]) {
            const points = [];
            for (let k = 0; k <= 8; k++) {
                const progress = k / 8;
                const v = t + 0.15 * progress;
                const u = 0.77 * progress;
                points.push(new THREE.Vector3(sign * u * width * Math.pow(Math.sin(Math.PI * v), 0.86), v * length, 0.16 * Math.sin(v * Math.PI * 0.9) + 0.095 * (1 - u) * Math.sin(Math.PI * v) + 0.024 * Math.sin(Math.PI * v) * (1 - u * u) + 0.002));
            }
            group.add(tube(points, 0.0026, materials.vein, `${name}Vein`));
        }
    return group;
}
function logoGeometry(shapes, width, depth, bevel) {
    const geometry = new THREE.ExtrudeGeometry(shapes, {
        depth, curveSegments: 40, bevelEnabled: true,
        bevelSize: bevel, bevelThickness: bevel, bevelSegments: 5, steps: 1,
    });
    geometry.computeBoundingBox();
    const bounds = geometry.boundingBox;
    const size = bounds.getSize(new THREE.Vector3());
    geometry.center();
    // Rotate the SVG Y axis into the 3D world while preserving handedness.
    geometry.scale(width / Math.max(size.x, size.y), -width / Math.max(size.x, size.y), -width / Math.max(size.x, size.y));
    return geometry;
}
export function createRefinedGPTFlower(logoPath) {
    const model = new THREE.Group();
    model.name = 'GPTFlowerRefined';
    const grain = clayTexture();
    const materials = {
        clay: new THREE.MeshStandardMaterial({ color: 0xa6674c, roughness: 0.82, normalMap: grain, normalScale: new THREE.Vector2(.23, .23) }),
        inner: new THREE.MeshStandardMaterial({ color: 0x8c4f39, roughness: 0.9, normalMap: grain, normalScale: new THREE.Vector2(.17, .17) }),
        sand: new THREE.MeshStandardMaterial({ color: 0xcdbc95, roughness: 0.93, normalMap: grain, normalScale: new THREE.Vector2(.36, .36) }),
        leaf: new THREE.MeshPhysicalMaterial({ color: 0x14794d, roughness: 0.4, clearcoat: 0.24, clearcoatRoughness: 0.42, side: THREE.DoubleSide }),
        vein: new THREE.MeshStandardMaterial({ color: 0x328356, roughness: 0.63 }),
        stem: new THREE.MeshStandardMaterial({ color: 0x166943, roughness: 0.48 }),
        black: new THREE.MeshPhysicalMaterial({ color: 0x131918, roughness: 0.42, clearcoat: 0.08, clearcoatRoughness: 0.4 }),
        ivory: new THREE.MeshPhysicalMaterial({ color: 0xf5f2e8, roughness: 0.48, clearcoat: 0.13 }),
        axle: new THREE.MeshStandardMaterial({ color: 0x3b4940, roughness: 0.45, metalness: 0.5 }),
    };
    for (const material of Object.values(materials)) {
        material.color.convertSRGBToLinear();
        material.envMapIntensity = 0.35;
    }
    model.add(lathe([
        [0, 0.035], [0.47, 0.035], [0.50, 0.046], [0.525, 0.075],
        [0.54, 0.125], [0.555, 0.23], [0.59, 0.46], [0.63, 0.7], [0.67, 0.935],
        [0.69, 1.005], [0.705, 1.03], [0.706, 1.066], [0.693, 1.083],
        [0.661, 1.091], [0.641, 1.073], [0.632, 1.042],
        [0.62, 0.946], [0.565, 0.65], [0.5, 0.24], [0.477, 0.151], [0, 0.151],
    ], materials.clay, 'HollowTerracottaPot'));
    model.add(lathe([
        [0.65, 1.014], [0.659, 1.073], [0.673, 1.093], [0.711, 1.099],
        [0.745, 1.088], [0.761, 1.067], [0.765, 1.037], [0.754, 1.010],
        [0.728, 0.992], [0.685, 0.994], [0.65, 1.014],
    ], materials.clay, 'RoundedPotLip'));
    // A low recessed foot grounds the pot without a large added saucer.
    model.add(lathe([[0.46, 0.036], [0.47, 0.018], [0.486, 0.013], [0.502, 0.022], [0.503, 0.051]], materials.inner, 'RecessedPotFoot'));
    const soil = mesh('SandSurface', new THREE.CylinderGeometry(0.626, 0.61, 0.055, 96), materials.sand);
    soil.position.y = 0.971;
    model.add(soil);
    const stemPoints = [new THREE.Vector3(0, 0.98, -0.08), new THREE.Vector3(-0.025, 1.39, -0.08), new THREE.Vector3(0.018, 1.92, -0.15), new THREE.Vector3(0, 2.77, -0.20)];
    model.add(tube(stemPoints, 0.073, materials.stem, 'RoundedStem'));
    model.add(makeLeaf('LeftLeaf', 1.0, 0.227, 0.95, 1.32, materials));
    model.add(makeLeaf('RightLeaf', 1.04, 0.24, -0.98, 1.38, materials));
    const hub = mesh('FlowerBearing', new THREE.CylinderGeometry(0.136, 0.136, 0.14, 48), materials.stem);
    hub.rotation.x = Math.PI / 2;
    hub.position.set(0, 2.78, -0.20);
    model.add(hub);
    const pin = mesh('RotationAxle', new THREE.CylinderGeometry(0.05, 0.05, 0.22, 32), materials.axle);
    pin.rotation.x = Math.PI / 2;
    pin.position.set(0, 2.78, -0.15);
    model.add(pin);
    // Headless geometry checks do not have a DOM parser; browsers use the exact vector.
    let shapes;
    if (typeof DOMParser === 'undefined') {
        const silhouette = new THREE.Shape();
        silhouette.absarc(160, 160, 160, 0, Math.PI * 2, false);
        shapes = [silhouette];
    }
    else {
        const svg = new SVGLoader().parse(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 320"><path d="${logoPath}"/></svg>`);
        shapes = [];
        for (const path of svg.paths)
            shapes.push(...SVGLoader.createShapes(path));
    }
    const silhouette = shapes.map(shape => { const clone = shape.clone(); clone.holes = []; return clone; });
    const flower = new THREE.Group();
    flower.name = 'rotatingFlower';
    flower.position.set(0, 2.78, 0);
    flower.add(mesh('IvoryInlay', logoGeometry(silhouette, 2.303, 9.5, 0.95), materials.ivory));
    const blackGeometry = logoGeometry(shapes, 2.32, 9, 1.0);
    const front = mesh('GPTLogoFront', blackGeometry, materials.black);
    front.position.z = 0.045;
    const back = mesh('GPTLogoBack', blackGeometry, materials.black);
    back.position.z = -0.045;
    flower.add(front, back);
    model.add(flower);
    model.userData.design = 'Approved GPT flower study 02';
    model.userData.rotationAxis = 'local Z, flower only';
    return model;
}
